import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Search, Filter, ChevronLeft, RefreshCw } from 'lucide-react';

const n   = v => Number(v) || 0;
const pct = (a,b) => n(b)>0 ? Math.round((n(a)/n(b))*100) : 0;
const fmt = d => { try { return d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'; } catch { return '—'; } };
const COLORS = ['#3B82F6','#10B981','#F59E0B','#8B5CF6','#06B6D4','#EC4899','#14B8A6','#F97316','#6366F1','#84CC16'];

function Bar({ value, color='#10B981', h=5 }) {
  return (
    <div className="w-full rounded-full overflow-hidden bg-gray-100" style={{height:h}}>
      <div className="h-full rounded-full transition-all" style={{width:`${Math.min(Math.max(n(value),0),100)}%`,background:color}}/>
    </div>
  );
}

export default function DashboardProjectOverviewPage() {
  const navigate = useNavigate();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/reports/dashboard/project-overview')
      .then(r => setData(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = data.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name?.toLowerCase().includes(q) || p.client_name?.toLowerCase().includes(q) || p.project_id?.toLowerCase().includes(q);
    const matchStatus = status === 'all' || (status === 'running' && n(p.days_left) >= 0 && n(p.overdue_tasks) === 0) || (status === 'delayed' && (n(p.days_left) < 0 || n(p.overdue_tasks) > 0)) || (status === 'completed' && p.status === 'completed');
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin')} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-500"/>
        </button>
        <div>
          <h1 className="text-xl font-black text-gray-900">Project Overview</h1>
          <p className="text-xs text-gray-400">{filtered.length} projects</p>
        </div>
        <button onClick={load} className="ml-auto p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <RefreshCw className={`w-4 h-4 text-gray-400 ${loading?'animate-spin':''}`}/>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] border border-gray-200 rounded-xl px-3 py-2">
          <Search className="w-4 h-4 text-gray-300 flex-shrink-0"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search project, client, code..."
            className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-300"/>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-300"/>
          <select value={status} onChange={e=>setStatus(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600 outline-none">
            <option value="all">All Status</option>
            <option value="running">Running</option>
            <option value="delayed">Delayed</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <p className="text-xs text-gray-400 ml-auto">{filtered.length} of {data.length} projects</p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"/>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-300 text-sm">No projects found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  {['#','Project Name','Client','Progress','Current Stage','Due Date','Days Left','Status','Health'].map(h=>(
                    <th key={h} className="text-left text-[11px] font-semibold text-gray-400 uppercase px-4 py-3 whitespace-nowrap tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((p,idx) => {
                  const progress  = pct(p.done_tasks, p.total_tasks);
                  const daysLeft  = p.days_left !== null ? Number(p.days_left) : null;
                  const overdue   = daysLeft !== null && daysLeft < 0;
                  const ot        = n(p.overdue_tasks);
                  const statusLbl = (overdue||ot>0) ? 'Delayed' : 'Running';
                  const health    = (overdue||ot>=3) ? 'High' : (ot>=1||(daysLeft!==null&&daysLeft<=6)) ? 'Medium' : 'Low';
                  const barColor  = overdue ? '#EF4444' : COLORS[idx%COLORS.length];
                  return (
                    <tr key={p.id} className="hover:bg-blue-50/20 cursor-pointer transition-colors"
                      onClick={()=>navigate(`/admin/projects/${p.id}`)}>
                      <td className="px-4 py-3 text-xs text-gray-400 font-semibold">{idx+1}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900 text-sm">{p.name}</p>
                        {p.project_id && <p className="text-xs text-gray-400 font-mono">{p.project_id}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{p.client_name||'—'}</td>
                      <td className="px-4 py-3 min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1"><Bar value={progress} color={barColor}/></div>
                          <span className="text-xs font-bold w-8 text-right" style={{color:barColor}}>{progress}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{p.current_stage||'—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(p.deadline)}</td>
                      <td className="px-4 py-3 font-bold text-sm whitespace-nowrap"
                        style={{color:daysLeft===null?'#9CA3AF':overdue?'#EF4444':daysLeft<=7?'#F59E0B':'#22C55E'}}>
                        {daysLeft===null?'—':overdue?`${Math.abs(daysLeft)}d ago`:`${daysLeft}d`}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusLbl==='Running'?'bg-green-100 text-green-700':'bg-red-100 text-red-600'}`}>
                          {statusLbl}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex text-xs font-semibold px-2.5 py-0.5 rounded-full ${health==='Low'?'bg-green-100 text-green-700':health==='Medium'?'bg-amber-100 text-amber-700':'bg-red-100 text-red-600'}`}>
                          {health}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
